/**
 * StateMachineDemoPanel — C066 상태 머신 데모 (queue #646 §R646-2 항목1)
 * 고정 데모 라이프사이클(DRAFT → SUBMITTED → APPROVED/REJECTED)을 in-memory 로 1회 빌드해
 * fire(state,event) 실호출. 부작용 0.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStateMachineDefinition, useStateMachineFire } from '../../hooks/useStateMachineDemo';
import {
  cardStyle, sectionTitleStyle, subtitleStyle, labelStyle, inputStyle, monoStyle,
  primaryButtonStyle, resultBoxStyle, DemoErrorState, DemoEmptyHint,
} from './demoShared';

const STATES = ['DRAFT', 'SUBMITTED', 'APPROVED', 'REJECTED'];
const EVENTS = ['SUBMIT', 'APPROVE', 'REJECT'];

export function StateMachineDemoPanel() {
  const { t } = useTranslation();
  const definitionQuery = useStateMachineDefinition();
  const fireMutation = useStateMachineFire();

  const [currentState, setCurrentState] = useState('DRAFT');
  const [event, setEvent] = useState('SUBMIT');

  function handleFire() {
    fireMutation.mutate({ currentState, event });
  }

  return (
    <section aria-labelledby="state-machine-panel-title">
      <h2 id="state-machine-panel-title" style={sectionTitleStyle}>{t('showcase.demo.stateMachine.title')}</h2>
      <p style={subtitleStyle}>{t('showcase.demo.stateMachine.subtitle')}</p>

      <div style={cardStyle}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
          <div>
            <label htmlFor="sm-current-state" style={labelStyle}>{t('showcase.demo.stateMachine.currentStateLabel')}</label>
            <select id="sm-current-state" value={currentState} onChange={(e) => setCurrentState(e.target.value)} style={inputStyle}>
              {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="sm-event" style={labelStyle}>{t('showcase.demo.stateMachine.eventLabel')}</label>
            <select id="sm-event" value={event} onChange={(e) => setEvent(e.target.value)} style={inputStyle}>
              {EVENTS.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>
        </div>
        <button
          type="button"
          onClick={handleFire}
          disabled={fireMutation.isPending}
          aria-busy={fireMutation.isPending}
          style={{ ...primaryButtonStyle, opacity: fireMutation.isPending ? 0.6 : 1 }}
        >
          {fireMutation.isPending ? t('showcase.demo.stateMachine.firing') : t('showcase.demo.stateMachine.fireButton')}
        </button>

        <div style={{ marginTop: 'var(--spacing-4)' }}>
          {fireMutation.isError && (
            <DemoErrorState title={t('showcase.demo.common.callFailed')} error={fireMutation.error} />
          )}
          {!fireMutation.isError && !fireMutation.data && <DemoEmptyHint text={t('showcase.demo.stateMachine.empty')} />}
          {fireMutation.data && !fireMutation.isError && (
            <div role="region" aria-label={t('showcase.demo.stateMachine.resultLabel')} style={resultBoxStyle}>
              <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--spacing-2) var(--spacing-4)', margin: 0, fontSize: 'var(--font-size-sm)' }}>
                <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.demo.stateMachine.nextStateLabel')}</dt>
                <dd style={{ margin: 0, ...monoStyle, color: 'var(--color-status-success)', fontWeight: 700 }}>{fireMutation.data.nextState}</dd>
                <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.demo.stateMachine.permittedEventsLabel')}</dt>
                <dd style={{ margin: 0, ...monoStyle }}>{fireMutation.data.permittedEvents.join(', ') || '—'}</dd>
              </dl>
            </div>
          )}
        </div>
      </div>

      {/* 전이 정의 전체(다이어그램용 테이블) */}
      {definitionQuery.data && (
        <div style={{ marginTop: 'var(--spacing-4)' }}>
          <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-2)' }}>
            {t('showcase.demo.stateMachine.transitionsLabel')}
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-xs)', fontFamily: 'var(--font-family-mono)' }}>
            <thead>
              <tr>
                {['source', 'event', 'target'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: 'var(--spacing-1) var(--spacing-2)', borderBottom: '1px solid var(--color-surface-border)', color: 'var(--color-text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {definitionQuery.data.transitions.map((tr, i) => (
                <tr key={i}>
                  <td style={{ padding: 'var(--spacing-1) var(--spacing-2)' }}>{tr.source}</td>
                  <td style={{ padding: 'var(--spacing-1) var(--spacing-2)' }}>{tr.event}</td>
                  <td style={{ padding: 'var(--spacing-1) var(--spacing-2)' }}>{tr.target}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
